import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {ReviewLifecycleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import openAICheckerPanel from 'notebook/ai-tools/openAICheckerPanel';
import {ReviewBadgeContext, ReviewErrorType} from 'notebook/reviews/ReviewBadgeContext';
import * as ReviewDAO from 'notebook/reviews/ReviewDAO';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {getReviewLifecycleActionTitle, isReviewableEntry} from 'notebook/reviews/ReviewUtil';
import {getEntryOrFileIdKey} from 'notebook/util/analytics/FileAnalyticsUtils';
import notifications from 'notifications';
import DocumentEditorLockUtil from 'realtime/util/DocumentEditorLockUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import EntityStore from 'stores/EntityStore';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_LIGHT, NEAR_BLACK} from 'ui-library/Colors';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';
import {OngoingTaskResponse} from 'util/TaskRunner';

import {REVIEW_ACTION_TYPE_TO_DISPLAY_TEXT} from './review-lifecycles/ReviewLifecycleConstants';
import {runSendForRLCReviewModal} from './review-lifecycles/runSendForRLCReviewModal';
import {
  ReviewGraphQL,
  ReviewLifecycleGraphQL,
  ReviewableGraphQL,
  ReviewBadgeDropdownOptionDetails,
  Review,
  ReviewerGraphQL,
} from './ReviewConstants';
import {EntityByStageId, IsTeamReassignableByStageId} from './ReviewLifecycleSummaryPanel';

export const RESEND_FOR_REVIEW_DROPDOWN_TEXT = 'Resend for review';
export const SEND_FOR_REVIEW_DROPDOWN_TEXT = 'Send for review';
export const CHECK_ENTRY_WITH_AI_DROPDOWN_TEXT = 'Check entry';

function getSendForReviewDropdownText(
  reviewLifecycle: ReviewLifecycleGraphQL,
  review: ReviewGraphQL | null
): string {
  if (reviewLifecycle?.currentVersion.lifecycleType === ReviewLifecycleType.SELF_REVIEW) {
    return REVIEW_ACTION_TYPE_TO_DISPLAY_TEXT[reviewLifecycle.currentVersion.reviewStages[0].action];
  }

  return review != null ? RESEND_FOR_REVIEW_DROPDOWN_TEXT : SEND_FOR_REVIEW_DROPDOWN_TEXT;
}

function getExistingReviewersByStage(review: ReviewGraphQL | null): EntityByStageId {
  return (
    review?.reviewers?.reduce((acc: EntityByStageId, reviewer: ReviewerGraphQL) => {
      acc[reviewer.reviewStage.id] = reviewer.user;
      return acc;
    }, {}) ?? {}
  );
}

function getExistingCanTeamReassignByStage(review: ReviewGraphQL | null): IsTeamReassignableByStageId {
  return (
    review?.reviewers?.reduce((acc: IsTeamReassignableByStageId, reviewer: ReviewerGraphQL) => {
      acc[reviewer.reviewStage.id] = reviewer.isTeamReassignable;
      return acc;
    }, {}) ?? {}
  );
}

async function performSelfReview(
  reviewableId: string,
  reviewLifecycleStageIds: Array<string>,
  onRequestComplete?: (
    reviewableId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void
): Promise<void> {
  const computingCellsError = ReviewUtil.getComputingCellsErrorMessage(reviewableId);
  if (computingCellsError) {
    notifications.error(computingCellsError);
    return;
  }

  assert(reviewLifecycleStageIds.length === 1, 'Self-review review lifecycle must have a single stage');
  const stageReviewers = [
    {
      stageId: reviewLifecycleStageIds[0],
      reviewerUserId: EntityStore.currentUser().id,
    },
  ];

  await ReviewUtil.ensureCurrentRuntimeSnapshotPersisted(reviewableId);
  const {review, filePermissions, snapshotTask} = await ReviewDAO.sendForRLCReview({
    fileApiId: reviewableId,
    message: '',
    rlcStageReviewersData: stageReviewers,
  });
  onRequestComplete?.(reviewableId, review, filePermissions, snapshotTask);
}

export function ReviewInProgressMenu({
  disableOptions,
  tooltipTitle,
}: {
  disableOptions: boolean;
  tooltipTitle: string | null;
}): JSX.Element {
  const {
    openHistory,
    reviewError,
    reviewable: _reviewable,
    onReviewChange,
    performanceMetadata,
    renderAdditionalReviewInfo,
    reviewableId,
  } = ReviewBadgeContext.useContext();
  const sendForReviewError = reviewError?.type === ReviewErrorType.COMPONENT ? reviewError.error : null;
  assert(
    Boolean(reviewableId && _reviewable && !isInaccessible(_reviewable)),
    'Reviewable must be defined and not inaccessible'
  );
  const reviewable = _reviewable as ReviewableGraphQL;
  const activeEditorLock = useSelector(
    (state: BenchlingState) => state.ui.documentEditorLockState[reviewableId!] ?? null
  );
  const isEntryEditLocked = activeEditorLock && DocumentEditorLockUtil.isLockedInRealtime(activeEditorLock);

  const createOnSelectHandler = useCallback(
    (reviewLifecycle: ReviewLifecycleGraphQL): (() => Promise<void>) =>
      async () => {
        // Review Lifecycle should be non-null when it's a self-review option.
        if (reviewLifecycle.currentVersion.lifecycleType === ReviewLifecycleType.SELF_REVIEW) {
          const reviewLifecycleStageIds = reviewLifecycle.currentVersion.reviewStages.map(
            (stage) => stage.id
          );
          const selfReviewAction = reviewLifecycle.currentVersion.reviewStages[0].action;
          const loadingMessage = `${getReviewLifecycleActionTitle(selfReviewAction)} Review`;
          try {
            LoadingScreen.show(loadingMessage);
            await performSelfReview(reviewableId!, reviewLifecycleStageIds, onReviewChange);
          } finally {
            LoadingScreen.hide();
          }
        } else {
          const fileIdKey = getEntryOrFileIdKey(reviewableId);
          runSendForRLCReviewModal({
            reviewableId: reviewableId!,
            onRequestComplete: onReviewChange,
            existingReviewerUsersByStage: getExistingReviewersByStage(reviewable.review),
            analyticsParams: {
              [fileIdKey]: reviewableId!,
              reviewable_id: reviewableId!,
              project_id: reviewable.reviewSource.id,
              template_id: isReviewableEntry(reviewable) ? (reviewable.template?.id ?? null) : null,
              template_collection_id: isReviewableEntry(reviewable)
                ? (reviewable.template?.templateCollection.id ?? null)
                : null,
            },
            existingCanTeamReassignByStage: getExistingCanTeamReassignByStage(reviewable.review),
            renderAdditionalReviewInfo,
            renderReviewError: reviewError?.type === ReviewErrorType.CALLBACK ? reviewError.error : undefined,
            performanceMetadata,
          });
        }
      },
    [onReviewChange, performanceMetadata, renderAdditionalReviewInfo, reviewError, reviewable, reviewableId]
  );

  const getSendForReviewDropdownOptionGroups = useCallback(() => {
    const reviewLifecycles = reviewable.parentReviewLifecycles;
    const options: Array<Array<ReviewBadgeDropdownOptionDetails>> = [];
    const parallelOrSequentialRLC = reviewLifecycles.find(({currentVersion: {lifecycleType}}) =>
      [ReviewLifecycleType.SEQUENTIAL, ReviewLifecycleType.PARALLEL].includes(lifecycleType)
    );
    const selfReviewRlcs = reviewLifecycles.filter(
      ({currentVersion: {lifecycleType}}) => lifecycleType === ReviewLifecycleType.SELF_REVIEW
    );

    if (parallelOrSequentialRLC != null) {
      options.push([
        {
          type: 'option',
          icon: 'right-from-bracket',
          text: getSendForReviewDropdownText(parallelOrSequentialRLC, reviewable.review),
          // TODO(BNCH-66037): If there is more than 1 parallel/sequential RLC, this is technically incorrect.
          onSelect: createOnSelectHandler(parallelOrSequentialRLC),
          dataTestAttrs: {
            component: 'ReviewInProgressButtonOption',
            element: 'root',
            key: 'sendForReview',
          },
        },
      ]);
    }
    const selfReviewOption: Array<ReviewBadgeDropdownOptionDetails> = [];
    if (selfReviewRlcs.length > 0) {
      if (options.length > 0) {
        // Only render section header for Self-Review if there is already a send for review option in the dropdown.
        selfReviewOption.push({
          type: 'header',
          text: 'Self-Review',
        });
      }
      selfReviewRlcs.forEach((selfReviewRlc) => {
        selfReviewOption.push({
          type: 'option',
          icon: 'file-circle-check',
          text: getSendForReviewDropdownText(selfReviewRlc, reviewable.review),
          onSelect: createOnSelectHandler(selfReviewRlc),
          isSelfReviewAction: true,
          dataTestAttrs: {
            component: 'ReviewInProgressButtonOption',
            element: 'root',
            key: 'selfReview',
          },
        });
      });
    }
    options.push(selfReviewOption);

    if (openHistory) {
      options.push([
        {
          type: 'option',
          icon: 'clipboard-list',
          text: 'See full history',
          onSelect: openHistory,
        },
      ]);
    }
    if (BenchlingConfig.ELN_ENABLE_AI_CHECKER && isReviewableEntry(reviewable)) {
      options.push([
        {
          type: 'option',
          icon: 'magnifying-glass',
          text: CHECK_ENTRY_WITH_AI_DROPDOWN_TEXT,
          onSelect: () => {
            openAICheckerPanel(reviewable.id, 'review-badge');
          },
        },
      ]);
    }
    return options;
  }, [reviewable, openHistory, createOnSelectHandler]);

  const optionGroups = useMemo(getSendForReviewDropdownOptionGroups, [getSendForReviewDropdownOptionGroups]);

  return (
    <Menu width={MenuWidth.FIT_CONTENT} className={css(reviewStyles.rightAlignedMenu)}>
      {optionGroups.map((options, groupIndex) => (
        <Section key={groupIndex}>
          {options.map((option, index) => {
            if (option.type === 'header') {
              return (
                <SectionHeader
                  className={css(reviewStyles.sectionHeader, reviewStyles.dropdownOption)}
                  key={index}
                >
                  {option.text}
                </SectionHeader>
              );
            }
            const {text, onSelect, dataTestAttrs, icon, isSelfReviewAction} = option;
            const disableReviewOption =
              isEntryEditLocked ||
              disableOptions ||
              // Disables actions tied to Self-Review Processes, when sendForReviewError is provided
              (isSelfReviewAction ? Boolean(sendForReviewError) : false);
            return (
              <Option
                key={index}
                onSelect={onSelect}
                dataTestAttrs={dataTestAttrs}
                isDisabled={disableReviewOption}
                icon={icon}
                tooltipTitle={tooltipTitle}
              >
                <div
                  className={css(
                    reviewStyles.dropdownOption,
                    ...(sendForReviewError ? [reviewStyles.dropdownOptionWithInlineBanner] : [])
                  )}
                >
                  {text}
                </div>
              </Option>
            );
          })}
        </Section>
      ))}

      {sendForReviewError && (
        <Option>
          <div className={css(reviewStyles.inlineBannerWrapper)}>
            <InlineBanner bannerType={InlineBannerType.ERROR} supportingInfo={sendForReviewError} />
          </div>
        </Option>
      )}
    </Menu>
  );
}

const reviewStyles = StyleSheet.create({
  dropdownOption: {
    marginLeft: -2,
    maxWidth: 180,
  },
  sectionHeader: {borderTop: `1px solid ${GRAY_LIGHT}`},
  dropdownOptionWithInlineBanner: {
    paddingBottom: 4,
    paddingTop: 4,
  },
  inlineBannerWrapper: {
    paddingTop: 4,
    color: NEAR_BLACK,
    maxWidth: '240px',
  },
  rightAlignedMenu: {
    right: 0,
  },
});
